import { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, Copy, Check, Share2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralWidget = ({ userId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!userId) return setLoading(false);
      try {
        let s = await axios.get(`${API}/referrals/mine/${userId}`);
        if (!s.data.referral_code) {
          await axios.post(`${API}/referrals/issue/${userId}`);
          s = await axios.get(`${API}/referrals/mine/${userId}`);
        }
        setStats(s.data);
      } catch (e) {
        console.error('referral load', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = stats?.referral_code ? `${origin}/intake?ref=${stats.referral_code}` : '';

  const copy = () => {
    if (!link) return;
    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const share = () => {
    if (navigator.share && link) {
      navigator.share({
        title: 'Queen B Logistics — Apply to Join',
        text: 'Premium freight dispatch for serious carriers. Apply with my referral link 👑',
        url: link,
      });
    } else copy();
  };

  if (loading || !stats?.referral_code) return null;

  return (
    <div
      className="bg-gradient-to-br from-[#D4AF37]/10 to-[#F5D47B]/5 border border-[#D4AF37]/30 p-5"
      data-testid="referral-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest">
          Refer a Carrier
        </h3>
      </div>
      <p className="text-white/70 text-sm mb-4 leading-relaxed">
        Earn <strong className="text-[#D4AF37]">$100 credit</strong> for every approved carrier who joins the Hive through your link.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat label="Referrals" value={stats.total_referrals} />
        <Stat label="Approved" value={stats.approved_referrals} />
        <Stat label="Earned" value={`$${stats.credit_earned}`} accent />
      </div>

      {/* Link */}
      <div className="bg-black/40 border border-white/10 p-2 flex items-center gap-2 mb-3 min-w-0">
        <input
          readOnly
          value={link}
          className="flex-1 min-w-0 bg-transparent text-white/70 text-xs font-mono focus:outline-none truncate"
          data-testid="referral-link-input"
          onClick={(e) => e.target.select()}
        />
        <button
          onClick={copy}
          className="p-2 hover:bg-white/10 flex-shrink-0"
          title="Copy"
          data-testid="copy-referral-btn"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-white/70" />}
        </button>
      </div>

      <button
        onClick={share}
        className="w-full py-2.5 bg-[#D4AF37] text-black font-bold text-sm uppercase tracking-wider hover:bg-[#F5D47B] flex items-center justify-center gap-2"
      >
        <Share2 className="w-4 h-4" />
        Share Your Link
      </button>

      <p className="text-white/30 text-[10px] mt-3 text-center">
        Your code: <span className="text-[#D4AF37] font-mono">{stats.referral_code}</span>
      </p>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="bg-black/30 border border-white/5 p-2 text-center">
    <p className={`text-lg font-black ${accent ? 'text-[#D4AF37]' : 'text-white'}`}>{value}</p>
    <p className="text-white/40 text-[10px] uppercase tracking-wider mt-0.5">{label}</p>
  </div>
);

export default ReferralWidget;
